import React, { useState, useEffect, useMemo } from "react";
import * as adminApi from "../../api/adminApi";
import { useToast } from "../../context/ToastContext";
import { formatMoney, formatProductSize } from "../../utils/format";
import { resolveImageUrl } from "../../utils/images";
import {
  X,
  Plus,
  Trash2,
  RefreshCw,
  Search,
  Package,
  Layers,
  Palette,
  Check,
  ArrowDownLeft,
  ArrowUpRight,
  Calculator,
  ShieldCheck,
} from "lucide-react";
import ProductSearchPicker from "./ProductSearchPicker";
import "./AdminExchangeModal.css";

export default function AdminExchangeModal({ isOpen, order, onClose, onSuccess }) {
  const { addToast } = useToast();

  // Step 1: Inbound Items (Customer Returns)
  const [inboundItems, setInboundItems] = useState([]);

  // Step 2: Replacements (Outbound Items)
  const [outboundItems, setOutboundItems] = useState([]);
  const [replacementMode, setReplacementMode] = useState("catalog"); // 'catalog' | 'order-variants'

  // Catalog Picker State
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedCatalogProduct, setSelectedCatalogProduct] = useState(null);
  const [catalogVariants, setCatalogVariants] = useState(null);
  const [loadingVariants, setLoadingVariants] = useState(false);

  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [replacementQty, setReplacementQty] = useState(1);

  // Quick Order Item Variants Cache
  const [orderVariantsCache, setOrderVariantsCache] = useState({});
  const [loadingOrderVariants, setLoadingOrderVariants] = useState({});
  const [pendingOrderReplacements, setPendingOrderReplacements] = useState({});

  // Financials & Options
  const [cod, setCod] = useState(0);
  const [allowToOpenPackage, setAllowToOpenPackage] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compute available quantity for returnable items in the order
  const getAvailableQuantity = (orderItem, shipments) => {
    let available = orderItem.quantity;
    if (!shipments) return available;
    shipments.forEach((shipment) => {
      if (shipment.status === "Cancelled") return;
      shipment.shipmentItems?.forEach((sItem) => {
        if (sItem.orderItemId === orderItem.id && sItem.direction === "Inbound") {
          available -= sItem.quantity;
        }
      });
    });
    return available > 0 ? available : 0;
  };

  // Initialize inbound items when order changes or modal opens
  useEffect(() => {
    if (isOpen && order) {
      const exchangeable = (order.orderItems || [])
        .map((oi) => ({
          ...oi,
          available: getAvailableQuantity(oi, order.shipments),
          selectedQuantity: 0,
        }))
        .filter((oi) => oi.available > 0);

      setInboundItems(exchangeable);
      setOutboundItems([]);
      setSelectedCatalogProduct(null);
      setCatalogVariants(null);
      setSelectedSizeId(null);
      setSelectedColorId(null);
      setReplacementQty(1);
      setCod(0);
      setAllowToOpenPackage(true);
    }
  }, [isOpen, order]);

  // Load product catalog for search picker
  useEffect(() => {
    if (isOpen && products.length === 0) {
      let cancelled = false;
      setLoadingProducts(true);
      adminApi
        .fetchAdminProducts()
        .then((res) => {
          if (!cancelled) setProducts(Array.isArray(res) ? res : []);
        })
        .catch((err) => {
          if (!cancelled) addToast(err.message || "Failed to load product catalog", "error");
        })
        .finally(() => {
          if (!cancelled) setLoadingProducts(false);
        });
      return () => {
        cancelled = true;
      };
    }
  }, [isOpen, products.length, addToast]);

  // When a catalog product is chosen
  const handleSelectCatalogProduct = async (product) => {
    setSelectedCatalogProduct(product);
    setCatalogVariants(null);
    setSelectedSizeId(null);
    setSelectedColorId(null);
    setReplacementQty(1);
    setLoadingVariants(true);

    try {
      const res = await adminApi.fetchProductVariantsByCode(product.code);
      setCatalogVariants(res);

      if (res.sizeVariants?.length === 1) setSelectedSizeId(res.sizeVariants[0].id);
      if (res.colorVariants?.length === 1) setSelectedColorId(res.colorVariants[0].id);
    } catch (err) {
      addToast(err.message || "Failed to load variants for this product", "error");
    } finally {
      setLoadingVariants(false);
    }
  };

  const handleClearCatalogProduct = () => {
    setSelectedCatalogProduct(null);
    setCatalogVariants(null);
    setSelectedSizeId(null);
    setSelectedColorId(null);
    setReplacementQty(1);
  };

  // Add chosen catalog product variant to outbound items
  const handleAddCatalogReplacement = () => {
    if (!selectedCatalogProduct || !catalogVariants) return;
    if (!selectedSizeId || !selectedColorId) {
      addToast("Please select both a size and a color variant", "error");
      return;
    }
    const qty = Math.max(1, Number(replacementQty) || 1);

    const sizeObj = catalogVariants.sizeVariants.find((s) => s.id === selectedSizeId);
    const colorObj = catalogVariants.colorVariants.find((c) => c.id === selectedColorId);

    setOutboundItems((prev) => {
      const existingIndex = prev.findIndex(
        (i) => i.sizeId === selectedSizeId && i.colorId === selectedColorId
      );

      if (existingIndex >= 0) {
        return prev.map((item, idx) =>
          idx === existingIndex ? { ...item, quantity: item.quantity + qty } : item
        );
      }

      return [
        ...prev,
        {
          productId: selectedCatalogProduct.id,
          productName: selectedCatalogProduct.name,
          arabicName: selectedCatalogProduct.arabicName,
          code: selectedCatalogProduct.code,
          thumbnailImage: selectedCatalogProduct.thumbnailImage,
          price: selectedCatalogProduct.basePrice || 0,
          sizeId: selectedSizeId,
          sizeName: sizeObj?.size || "",
          colorId: selectedColorId,
          colorName: colorObj?.color || "",
          quantity: qty,
        },
      ];
    });

    addToast(`Added ${selectedCatalogProduct.name} to replacements`, "success");
    // Reset variant pickers for next addition
    setSelectedSizeId(catalogVariants.sizeVariants?.length === 1 ? catalogVariants.sizeVariants[0].id : null);
    setSelectedColorId(catalogVariants.colorVariants?.length === 1 ? catalogVariants.colorVariants[0].id : null);
    setReplacementQty(1);
  };

  // Load variants for an order item in "order-variants" mode
  const loadVariantsForOrderItem = async (code) => {
    if (orderVariantsCache[code] || loadingOrderVariants[code]) return;
    setLoadingOrderVariants((prev) => ({ ...prev, [code]: true }));
    try {
      const res = await adminApi.fetchProductVariantsByCode(code);
      setOrderVariantsCache((prev) => ({ ...prev, [code]: res }));
    } catch (err) {
      addToast(`Failed to load variants for ${code}`, "error");
    } finally {
      setLoadingOrderVariants((prev) => ({ ...prev, [code]: false }));
    }
  };

  // Add same-item replacement
  const handleAddOrderReplacement = (orderItem) => {
    const pending = pendingOrderReplacements[orderItem.id];
    if (!pending || !pending.sizeId || !pending.colorId) {
      addToast("Please select a size and color", "error");
      return;
    }
    const variants = orderVariantsCache[orderItem.code];
    if (!variants) return;

    const sizeObj = variants.sizeVariants.find((s) => s.id === pending.sizeId);
    const colorObj = variants.colorVariants.find((c) => c.id === pending.colorId);
    const qty = Math.max(1, pending.qty || 1);

    setOutboundItems((prev) => {
      const existingIndex = prev.findIndex(
        (i) => i.sizeId === pending.sizeId && i.colorId === pending.colorId
      );

      if (existingIndex >= 0) {
        return prev.map((item, idx) =>
          idx === existingIndex ? { ...item, quantity: item.quantity + qty } : item
        );
      }

      return [
        ...prev,
        {
          productId: orderItem.productId,
          productName: orderItem.productName,
          code: orderItem.code,
          thumbnailImage: orderItem.colorImageUrl,
          price: orderItem.price,
          sizeId: pending.sizeId,
          sizeName: sizeObj?.size || "",
          colorId: pending.colorId,
          colorName: colorObj?.color || "",
          quantity: qty,
        },
      ];
    });

    setPendingOrderReplacements((prev) => ({ ...prev, [orderItem.id]: null }));
    addToast(`Added ${orderItem.productName} replacement`, "success");
  };

  // Update Inbound Qty
  const updateInboundQuantity = (itemId, qty) => {
    setInboundItems((prev) =>
      prev.map((i) =>
        i.id === itemId ? { ...i, selectedQuantity: Math.min(Math.max(0, qty), i.available) } : i
      )
    );
  };

  // Remove replacement
  const removeOutboundItem = (index) => {
    setOutboundItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Update outbound item quantity
  const updateOutboundQuantity = (index, qty) => {
    const val = Math.max(1, parseInt(qty) || 1);
    setOutboundItems((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, quantity: val } : item))
    );
  };

  // Financial calculations
  const totalInboundValue = useMemo(() => {
    return inboundItems.reduce((acc, item) => acc + (item.price || 0) * (item.selectedQuantity || 0), 0);
  }, [inboundItems]);

  const totalOutboundValue = useMemo(() => {
    return outboundItems.reduce((acc, item) => acc + (item.price || 0) * (item.quantity || 0), 0);
  }, [outboundItems]);

  const balanceDelta = useMemo(() => {
    return totalOutboundValue - totalInboundValue;
  }, [totalOutboundValue, totalInboundValue]);

  // Submit exchange to backend
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!order) return;

    const returnItems = inboundItems
      .filter((i) => i.selectedQuantity > 0)
      .map((i) => ({ orderItemGuid: i.id, quantity: Number(i.selectedQuantity) }));

    const replacements = outboundItems.map((i) => ({
      sizeId: i.sizeId,
      colorId: i.colorId,
      quantity: Number(i.quantity),
    }));

    if (returnItems.length === 0 && replacements.length === 0) {
      addToast("Please select at least one item to return or replace", "error");
      return;
    }

    const payload = {
      inboundOrderItemIds: returnItems,
      outboundItems: replacements,
      cod: Number(cod) || 0,
      allowToOpenPackage: Boolean(allowToOpenPackage),
    };

    setIsSubmitting(true);
    try {
      await adminApi.adminPartialExchange(order.id, payload);
      addToast("Exchange order initiated successfully!", "success");
      onSuccess?.();
      onClose();
    } catch (err) {
      addToast(err.message || "Failed to process exchange", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !order) return null;

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <div className="admin-modal exchange-modal">
        {/* Fixed Header */}
        <div className="exchange-modal__head">
          <div>
            <h3 style={{ margin: 0, fontSize: "1.4rem" }}>Exchange Order Items</h3>
            <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "var(--text-muted)" }}>
              Order #{String(order.id).slice(0, 8)} · Select customer returns & replacement items.
            </p>
          </div>
          <button
            type="button"
            className="admin-icon-link"
            onClick={onClose}
            aria-label="Close exchange modal"
            style={{ padding: "8px" }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Scrollable Modal Body */}
        <div className="exchange-modal__body">
          {/* STEP 1: Inbound Items (Returns) */}
          <div className="exchange-section">
            <div className="exchange-section__title-row">
              <h4 className="exchange-section__title">
                <ArrowDownLeft size={16} style={{ color: "#8b4545" }} />
                1. Inbound Items (Customer Returns)
              </h4>
              <span className="exchange-section__badge">
                Return Value: {formatMoney(totalInboundValue)}
              </span>
            </div>

            {inboundItems.length === 0 ? (
              <p style={{ color: "var(--text-muted)", fontSize: "13px", margin: "12px 0" }}>
                No exchangeable items available in this order.
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="exchange-outbound-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Unit Price</th>
                      <th>Available</th>
                      <th style={{ width: "120px" }}>Qty to Return</th>
                      <th style={{ textAlign: "right", width: "100px" }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inboundItems.map((item) => {
                      const isSelected = item.selectedQuantity > 0;
                      return (
                        <tr
                          key={item.id}
                          style={{
                            background: isSelected ? "var(--surface-accent)" : "transparent",
                          }}
                        >
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div
                                style={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "var(--radius)",
                                  border: "1px solid var(--border)",
                                  background: item.colorImageUrl
                                    ? `url(${resolveImageUrl(item.colorImageUrl)}) center/cover`
                                    : "var(--surface-input)",
                                }}
                              />
                              <div>
                                <div style={{ fontWeight: 500, fontSize: "13px" }}>{item.productName}</div>
                                <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                                  {[formatProductSize(item.productSize), item.color].filter(Boolean).join(" · ")}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>{formatMoney(item.price)}</td>
                          <td>{item.available}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={item.available}
                              value={item.selectedQuantity}
                              onChange={(e) => updateInboundQuantity(item.id, parseInt(e.target.value) || 0)}
                              style={{
                                width: "80px",
                                padding: "6px 10px",
                                border: isSelected ? "1.5px solid var(--gold)" : "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                textAlign: "center",
                                fontFamily: "monospace",
                                fontWeight: isSelected ? 600 : "normal",
                                background: "var(--surface-input)",
                                color: "var(--text-primary)",
                              }}
                            />
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 500 }}>
                            {formatMoney((item.price || 0) * (item.selectedQuantity || 0))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* STEP 2: Pick Replacements (Store Catalog OR Order Variants) */}
          <div className="exchange-section">
            <div className="exchange-section__title-row">
              <h4 className="exchange-section__title">
                <ArrowUpRight size={16} style={{ color: "var(--gold-dark)" }} />
                2. Select Replacement Items
              </h4>
              <span className="exchange-section__badge">
                Replacements Value: {formatMoney(totalOutboundValue)}
              </span>
            </div>

            {/* Mode Selector Tabs */}
            <div className="exchange-tabs">
              <button
                type="button"
                className={`exchange-tab-btn ${replacementMode === "catalog" ? "is-active" : ""}`}
                onClick={() => setReplacementMode("catalog")}
              >
                <Search size={15} /> Browse & Search Full Store Catalog
              </button>
              <button
                type="button"
                className={`exchange-tab-btn ${replacementMode === "order-variants" ? "is-active" : ""}`}
                onClick={() => setReplacementMode("order-variants")}
              >
                <RefreshCw size={15} /> Quick Same-Product Size/Color Swap
              </button>
            </div>

            {/* Tab 1: Full Catalog Product Search Picker */}
            {replacementMode === "catalog" && (
              <div>
                {!selectedCatalogProduct ? (
                  <div style={{ height: "360px", display: "flex", flexDirection: "column" }}>
                    <ProductSearchPicker
                      products={products}
                      selectedProduct={null}
                      onSelectProduct={handleSelectCatalogProduct}
                      onClearProduct={handleClearCatalogProduct}
                      loading={loadingProducts}
                    />
                  </div>
                ) : (
                  <div>
                    <ProductSearchPicker
                      products={products}
                      selectedProduct={selectedCatalogProduct}
                      onSelectProduct={handleSelectCatalogProduct}
                      onClearProduct={handleClearCatalogProduct}
                      loading={loadingProducts}
                    />

                    {loadingVariants ? (
                      <div style={{ padding: "24px", textAlign: "center", color: "var(--text-muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontSize: "13px" }}>
                        <span className="page-loading__dot" style={{ width: "8px", height: "8px" }} />
                        Loading product variants...
                      </div>
                    ) : catalogVariants && (
                      <div className="exchange-variant-picker">
                        {/* Sizes */}
                        <div>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-secondary)", marginBottom: "8px" }}>
                            Choose Size Variant
                          </label>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {catalogVariants.sizeVariants?.map((s) => {
                              const isSelected = selectedSizeId === s.id;
                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  onClick={() => setSelectedSizeId(s.id)}
                                  style={{
                                    padding: "8px 14px",
                                    border: isSelected ? "2px solid var(--gold)" : "1px solid var(--border)",
                                    background: isSelected ? "var(--gold)" : "var(--surface-card)",
                                    color: isSelected ? "#ffffff" : "var(--text-primary)",
                                    fontWeight: isSelected ? 600 : 400,
                                    borderRadius: "var(--radius)",
                                    fontSize: "13px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    cursor: "pointer",
                                  }}
                                >
                                  {isSelected && <Check size={14} style={{ color: "#ffffff" }} />}
                                  {formatProductSize(s.size)}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Colors */}
                        <div>
                          <label style={{ display: "block", fontSize: "12px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--text-secondary)", marginBottom: "8px" }}>
                            Choose Color Variant
                          </label>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                            {catalogVariants.colorVariants?.map((c) => {
                              const isSelected = selectedColorId === c.id;
                              return (
                                <button
                                  key={c.id}
                                  type="button"
                                  onClick={() => setSelectedColorId(c.id)}
                                  style={{
                                    padding: "8px 14px",
                                    border: isSelected ? "2px solid var(--gold)" : "1px solid var(--border)",
                                    background: isSelected ? "var(--gold)" : "var(--surface-card)",
                                    color: isSelected ? "#ffffff" : "var(--text-primary)",
                                    fontWeight: isSelected ? 600 : 400,
                                    borderRadius: "var(--radius)",
                                    fontSize: "13px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    cursor: "pointer",
                                  }}
                                >
                                  {isSelected && <Check size={14} style={{ color: "#ffffff" }} />}
                                  {c.color}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Quantity & Add Action */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <label style={{ fontSize: "13px", fontWeight: 500 }}>Quantity to Send:</label>
                            <input
                              type="number"
                              min="1"
                              value={replacementQty}
                              onChange={(e) => setReplacementQty(Math.max(1, parseInt(e.target.value) || 1))}
                              style={{
                                width: "70px",
                                padding: "6px 8px",
                                textAlign: "center",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                fontFamily: "monospace",
                                fontSize: "14px",
                                background: "var(--surface-card)",
                                color: "var(--text-primary)",
                              }}
                            />
                            <button
                              type="button"
                              className="btn-outline"
                              onClick={() => setReplacementQty((q) => q + 1)}
                              style={{ padding: "6px 10px", fontSize: "12px" }}
                            >
                              +1
                            </button>
                          </div>

                          <button
                            type="button"
                            className="btn-primary"
                            onClick={handleAddCatalogReplacement}
                            disabled={!selectedSizeId || !selectedColorId}
                            style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 18px" }}
                          >
                            <Plus size={16} /> Add to Replacement Items
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tab 2: Quick Same-Product Variant Swap */}
            {replacementMode === "order-variants" && (
              <div>
                <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "12px" }}>
                  Quickly pick an alternative size or color for items in this order:
                </p>

                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {inboundItems.map((item) => {
                    const variants = orderVariantsCache[item.code];
                    const isLoading = loadingOrderVariants[item.code];
                    const pending = pendingOrderReplacements[item.id] || { colorId: "", sizeId: "", qty: 1 };

                    return (
                      <div
                        key={item.id}
                        style={{
                          background: "var(--surface-input)",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius)",
                          padding: "14px",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                          <div>
                            <strong style={{ fontSize: "14px" }}>{item.productName}</strong>
                            <span style={{ fontSize: "12px", color: "var(--text-muted)", marginLeft: "8px" }}>
                              (Code: {item.code}) · {formatMoney(item.price)}
                            </span>
                          </div>

                          {!variants && (
                            <button
                              type="button"
                              className="btn-outline"
                              onClick={() => loadVariantsForOrderItem(item.code)}
                              disabled={isLoading}
                              style={{ padding: "4px 10px", fontSize: "12px" }}
                            >
                              {isLoading ? "Loading..." : "Load Sizes & Colors"}
                            </button>
                          )}
                        </div>

                        {variants && (
                          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                            <select
                              value={pending.colorId}
                              onChange={(e) =>
                                setPendingOrderReplacements((prev) => ({
                                  ...prev,
                                  [item.id]: { ...pending, colorId: e.target.value },
                                }))
                              }
                              style={{
                                padding: "6px 10px",
                                fontSize: "13px",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                minWidth: "120px",
                                background: "var(--surface-card)",
                                color: "var(--text-primary)",
                              }}
                            >
                              <option value="">-- Choose Color --</option>
                              {variants.colorVariants?.map((c) => (
                                <option key={c.id} value={c.id}>{c.color || c.name}</option>
                              ))}
                            </select>

                            <select
                              value={pending.sizeId}
                              onChange={(e) =>
                                setPendingOrderReplacements((prev) => ({
                                  ...prev,
                                  [item.id]: { ...pending, sizeId: e.target.value },
                                }))
                              }
                              style={{
                                padding: "6px 10px",
                                fontSize: "13px",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                minWidth: "120px",
                                background: "var(--surface-card)",
                                color: "var(--text-primary)",
                              }}
                            >
                              <option value="">-- Choose Size --</option>
                              {variants.sizeVariants?.map((s) => (
                                <option key={s.id} value={s.id}>{formatProductSize(s.size || s.sizeName)}</option>
                              ))}
                            </select>

                            <input
                              type="number"
                              min="1"
                              value={pending.qty}
                              onChange={(e) =>
                                setPendingOrderReplacements((prev) => ({
                                  ...prev,
                                  [item.id]: { ...pending, qty: Math.max(1, parseInt(e.target.value) || 1) },
                                }))
                              }
                              style={{
                                width: "60px",
                                padding: "6px",
                                textAlign: "center",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                fontSize: "13px",
                                background: "var(--surface-card)",
                                color: "var(--text-primary)",
                              }}
                            />

                            <button
                              type="button"
                              className="btn-primary"
                              onClick={() => handleAddOrderReplacement(item)}
                              disabled={!pending.sizeId || !pending.colorId}
                              style={{ padding: "6px 14px", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "6px" }}
                            >
                              <Plus size={14} /> Add
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* List of Finalized Outbound Items */}
            {outboundItems.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <h5 style={{ margin: "0 0 10px 0", fontSize: "13px", color: "var(--text-primary)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Replacements Added ({outboundItems.length})
                </h5>
                <div style={{ border: "1px solid var(--border)", borderRadius: "var(--radius)", overflowX: "auto" }}>
                  <table className="exchange-outbound-table">
                    <thead>
                      <tr>
                        <th>Replacement Item</th>
                        <th>Color</th>
                        <th>Size</th>
                        <th>Price</th>
                        <th style={{ width: "100px" }}>Qty</th>
                        <th style={{ textAlign: "right" }}>Total</th>
                        <th style={{ width: "50px" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {outboundItems.map((item, idx) => (
                        <tr key={`${item.sizeId}-${item.colorId}`}>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                              <div
                                style={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "var(--radius)",
                                  border: "1px solid var(--border)",
                                  background: item.thumbnailImage
                                    ? `url(${resolveImageUrl(item.thumbnailImage)}) center/cover`
                                    : "var(--surface-input)",
                                }}
                              />
                              <span style={{ fontWeight: 500 }}>{item.productName}</span>
                            </div>
                          </td>
                          <td>{item.colorName}</td>
                          <td>{formatProductSize(item.sizeName)}</td>
                          <td>{formatMoney(item.price)}</td>
                          <td>
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateOutboundQuantity(idx, e.target.value)}
                              style={{
                                width: "65px",
                                padding: "4px 8px",
                                textAlign: "center",
                                border: "1px solid var(--border)",
                                borderRadius: "var(--radius)",
                                background: "var(--surface-input)",
                                color: "var(--text-primary)",
                              }}
                            />
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 500 }}>
                            {formatMoney((item.price || 0) * (item.quantity || 0))}
                          </td>
                          <td>
                            <button
                              type="button"
                              onClick={() => removeOutboundItem(idx)}
                              style={{ color: "#8b4545", padding: "4px" }}
                              title="Remove item"
                            >
                              <Trash2 size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* STEP 3: Financial Settlement & Options */}
          <div className="exchange-section">
            <div className="exchange-section__title-row">
              <h4 className="exchange-section__title">
                <Calculator size={16} style={{ color: "var(--gold-dark)" }} />
                3. Financial Settlement & COD Balance
              </h4>
            </div>

            {/* Financial Summary Grid */}
            <div className="exchange-summary-card">
              <div className="exchange-summary-col">
                <span className="exchange-summary-label">Returned Value (Inbound)</span>
                <span className="exchange-summary-value">{formatMoney(totalInboundValue)}</span>
              </div>
              <div className="exchange-summary-col">
                <span className="exchange-summary-label">Replacements Value (Outbound)</span>
                <span className="exchange-summary-value">{formatMoney(totalOutboundValue)}</span>
              </div>
              <div className="exchange-summary-col">
                <span className="exchange-summary-label">Calculated Difference (Δ)</span>
                <span
                  className="exchange-summary-value"
                  style={{
                    color: balanceDelta > 0 ? "var(--gold-dark)" : balanceDelta < 0 ? "#8b4545" : "var(--text-primary)",
                  }}
                >
                  {balanceDelta > 0 ? `+${formatMoney(balanceDelta)}` : formatMoney(balanceDelta)}
                </span>
                <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                  {balanceDelta > 0
                    ? "Customer pays difference"
                    : balanceDelta < 0
                    ? "Store refunds difference"
                    : "Equal value swap"}
                </span>
              </div>
            </div>

            {/* COD Input row */}
            <div style={{ display: "flex", alignItems: "flex-end", gap: "16px", marginTop: "16px", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px" }}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 500, marginBottom: "6px" }}>
                  COD Amount to Collect / Refund
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={cod}
                  onChange={(e) => setCod(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    border: "1.5px solid var(--border)",
                    borderRadius: "var(--radius)",
                    fontFamily: "monospace",
                    fontSize: "16px",
                    fontWeight: 600,
                    background: "var(--surface-input)",
                    color: "var(--text-primary)",
                  }}
                  required
                />
              </div>

              <button
                type="button"
                className="btn-outline"
                onClick={() => setCod(balanceDelta)}
                style={{ padding: "10px 16px", fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "6px" }}
              >
                <Calculator size={14} /> Auto-Set to Δ ({formatMoney(balanceDelta)})
              </button>
            </div>

            {/* Bosta Package Policy */}
            <div style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                id="allow-open-pkg"
                type="checkbox"
                checked={allowToOpenPackage}
                onChange={(e) => setAllowToOpenPackage(e.target.checked)}
                style={{ width: "16px", height: "16px", accentColor: "var(--gold)" }}
              />
              <label htmlFor="allow-open-pkg" style={{ fontSize: "13px", color: "var(--text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                <ShieldCheck size={16} style={{ color: "var(--gold-dark)" }} />
                Allow customer to inspect and open package before accepting delivery (Bosta exchange policy)
              </label>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="exchange-modal__foot">
          <button type="button" className="btn-outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              inboundItems.filter((i) => i.selectedQuantity > 0).length === 0 ||
              outboundItems.length === 0
            }
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "0 28px" }}
          >
            {isSubmitting ? "Processing..." : "Confirm Exchange Shipment"}
          </button>
        </div>
      </div>
    </div>
  );
}
